import React, { useState, useEffect } from "react";
import { Card, Checkbox, Text } from "@sekmet/radix-ui-themes";
import clsx from "clsx";
import { useAppDispatch } from "store/store";
import { setAgoricSelectedChains, setAgoricSelectedDenom } from "store/create-survey/survey";
import { useDebounce } from "hooks";

export enum AgoricChainsEnum {
  AVALANCHE = 'Avalanche',
  OSMOSIS = 'Osmosis',
  NEUTRON = 'Neutron',
}

interface ChainOption {
  name: string;
  id: AgoricChainsEnum;
  icon: string;
  disabled?: boolean;
}

const SUPPORTED_CHAINS: ChainOption[] = [
  // {
  //   name: "Avalanche",
  //   id: AgoricChainsEnum.AVALANCHE,
  //   icon: "/images/avalanche.png",
  //   disabled: false,
  // },
  {
    name: "Osmosis",
    id: AgoricChainsEnum.OSMOSIS,
    icon: "/images/osmosis.png",
  },
  {
    name: "Neutron",
    id: AgoricChainsEnum.NEUTRON,
    icon: "/images/neutron.png",
  },
];

const DENOM_OPTIONS = [
  { key: "ubld", value: "BLD" },
  // { key: "uatom", value: "ATOM" },
];



const AgoricChainSelector = () => {
  const [selectedChains, setSelectedChains] = useState<AgoricChainsEnum[]>([]);
  const [selectedDenom, setSelectedDenom] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  const debouncedValueChains = useDebounce(selectedChains, 1000);
  const debouncedValueDenom = useDebounce(selectedDenom, 1000);

  useEffect(() => {
    // Update Redux store with selected chains
    if (debouncedValueChains.length > 0) {

      dispatch(setAgoricSelectedChains(debouncedValueChains));
    }
  }, [debouncedValueChains, dispatch]);

  useEffect(() => {
    if (debouncedValueDenom) {
      dispatch(setAgoricSelectedDenom(debouncedValueDenom));
    }
  }, [debouncedValueDenom, dispatch]);

  const handleChainToggle = (chainId: AgoricChainsEnum, disabled?: boolean) => {
    if (disabled) return;
    setSelectedChains(prev =>
      prev.includes(chainId) ? prev.filter(c => c !== chainId) : [...prev, chainId],
    );
  };

  const handleDenomSelect = (denomKey: string) => {
    setSelectedDenom(selectedDenom === denomKey ? null : denomKey);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Chain Selection */}
      <div>
        <Text size="2" weight="bold" className="block mb-3 text-zinc-900 dark:text-white">
          Select Chains (minimum 1)
        </Text>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {SUPPORTED_CHAINS.map(chain => (
            <Card
              key={chain.id}
              size="2"
              className={clsx(
                "transition-all",
                chain.disabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer",
                selectedChains.includes(chain.id)
                  ? "ring-2 ring-purple-500 dark:ring-purple-400"
                  : !chain.disabled && "hover:shadow-md",
              )}
              onClick={() => handleChainToggle(chain.id, chain.disabled)}
            >
              <div className="flex items-center justify-between w-full">
                <span className="flex items-center gap-3">
                  <img width="32" height="32" src={chain.icon} alt={chain.name} className="rounded-full" />
                  <Text size="2" weight="medium" className="text-zinc-900 dark:text-white">
                    {chain.name}
                  </Text>
                </span>
                <Checkbox checked={selectedChains.includes(chain.id)} disabled={chain.disabled} />
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Denom Selection */}
      {selectedChains.length > 0 && (
        <div>
          <Text size="2" weight="bold" className="block mb-3 text-zinc-900 dark:text-white">
            Select Denom
          </Text>
          <div className="flex gap-3">
            {DENOM_OPTIONS.map(denom => (
              <button
                key={denom.key}
                type="button"
                onClick={() => handleDenomSelect(denom.key)}
                className={clsx(
                  "flex-1 rounded-xl px-6 py-3 text-sm font-semibold transition-all",
                  selectedDenom === denom.key
                    ? "bg-purple-600 text-white shadow-lg"
                    : "bg-zinc-100 text-zinc-900 hover:bg-zinc-200 dark:bg-zinc-700 dark:text-white dark:hover:bg-zinc-600",
                )}
              >
                {denom.value}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Validation Message */}
      {selectedChains.length === 0 && (
        <Text size="1" className="text-red-500">
          Please select at least one chain
        </Text>
      )}
    </div>
  );
};

export default AgoricChainSelector;
